import { apiClient } from './client';
import type {
  DiagnosticForm,
  DiagnosticFormResponse,
  DiagnosticFormsResponse,
  DiagnosticTemplatesResponse,
  FormPortal,
  FormResponse,
  FormDiagnostic,
  FormResponsesListResponse,
  FormDiagnosticsListResponse,
  CreateDiagnosticFormRequest,
  UpdateDiagnosticFormRequest,
  AssociateFormRequest,
  UpdateFormPortalRequest,
  GenerateFormRequest,
  GenerateAnalysisRequest,
  DiagnosticFramework,
  FormStatus,
  PublicFormInfoResponse,
  PublicFormStructureResponse,
  StartResponseRequest,
  UpdateResponseRequest,
} from '@affogato/shared';

export const diagnosticsApi = {
  // ========== FORM MANAGEMENT ==========

  async getAll(params?: {
    clientId?: string;
    status?: FormStatus;
    framework?: DiagnosticFramework;
  }): Promise<DiagnosticFormsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.clientId) searchParams.append('clientId', params.clientId);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.framework) searchParams.append('framework', params.framework);
    const queryString = searchParams.toString();
    return apiClient.get(`/diagnostics${queryString ? `?${queryString}` : ''}`);
  },

  async getById(id: string): Promise<DiagnosticFormResponse> {
    return apiClient.get(`/diagnostics/${id}`);
  },

  async getTemplates(): Promise<DiagnosticTemplatesResponse> {
    return apiClient.get('/diagnostics/templates');
  },

  async create(data: CreateDiagnosticFormRequest): Promise<{ form: DiagnosticForm }> {
    return apiClient.post('/diagnostics', data);
  },

  async createFromTemplate(templateId: string): Promise<{ form: DiagnosticForm }> {
    return apiClient.post(`/diagnostics/from-template/${templateId}`);
  },

  async update(id: string, data: UpdateDiagnosticFormRequest): Promise<{ form: DiagnosticForm }> {
    return apiClient.put(`/diagnostics/${id}`, data);
  },

  async delete(id: string): Promise<{ success: boolean }> {
    return apiClient.delete(`/diagnostics/${id}`);
  },

  async duplicate(id: string): Promise<{ form: DiagnosticForm }> {
    return apiClient.post(`/diagnostics/${id}/duplicate`);
  },

  async publish(id: string): Promise<{ success: boolean; status: string }> {
    return apiClient.patch(`/diagnostics/${id}/publish`);
  },

  async archive(id: string): Promise<{ success: boolean; status: string }> {
    return apiClient.patch(`/diagnostics/${id}/archive`);
  },

  async associate(id: string, data: AssociateFormRequest): Promise<{
    success: boolean;
    clientId: string;
    projectId: string;
    projectName: string;
  }> {
    return apiClient.patch(`/diagnostics/${id}/associate`, data);
  },

  // ========== AI GENERATION ==========

  async generateForm(data: GenerateFormRequest): Promise<{
    title: string;
    description: string;
    structure: unknown;
  }> {
    return apiClient.post('/diagnostics/generate', data);
  },

  // ========== PORTAL MANAGEMENT ==========

  async getPortal(formId: string): Promise<{ portal: FormPortal | null }> {
    return apiClient.get(`/diagnostics/${formId}/portal`);
  },

  async createOrUpdatePortal(formId: string, data: UpdateFormPortalRequest): Promise<{ portal: FormPortal }> {
    return apiClient.post(`/diagnostics/${formId}/portal`, data);
  },

  async regeneratePortalToken(formId: string): Promise<{ token: string }> {
    return apiClient.post(`/diagnostics/${formId}/portal/regenerate`);
  },

  // ========== RESPONSES ==========

  async getResponses(formId: string): Promise<FormResponsesListResponse> {
    return apiClient.get(`/diagnostics/${formId}/responses`);
  },

  async getResponse(formId: string, responseId: string): Promise<{ response: FormResponse }> {
    return apiClient.get(`/diagnostics/${formId}/responses/${responseId}`);
  },

  async deleteResponse(formId: string, responseId: string): Promise<{ success: boolean }> {
    return apiClient.delete(`/diagnostics/${formId}/responses/${responseId}`);
  },

  // ========== DIAGNOSTICS/ANALYSIS ==========

  async generateAnalysis(formId: string, data: GenerateAnalysisRequest): Promise<{ diagnostic: FormDiagnostic }> {
    return apiClient.post(`/diagnostics/${formId}/analyze`, data);
  },

  async getAnalysisList(formId: string): Promise<FormDiagnosticsListResponse> {
    return apiClient.get(`/diagnostics/${formId}/analysis`);
  },

  async getAnalysis(formId: string, diagnosticId: string): Promise<{ diagnostic: FormDiagnostic }> {
    return apiClient.get(`/diagnostics/${formId}/analysis/${diagnosticId}`);
  },

  async updateAnalysis(
    formId: string,
    diagnosticId: string,
    data: { title?: string; content?: string; status?: 'draft' | 'published' }
  ): Promise<{ diagnostic: FormDiagnostic }> {
    return apiClient.patch(`/diagnostics/${formId}/analysis/${diagnosticId}`, data);
  },

  async deleteAnalysis(formId: string, diagnosticId: string): Promise<{ success: boolean }> {
    return apiClient.delete(`/diagnostics/${formId}/analysis/${diagnosticId}`);
  },
};

// ========== PUBLIC FORM API (No auth required) ==========

export const publicFormApi = {
  async getInfo(token: string): Promise<PublicFormInfoResponse> {
    return apiClient.get(`/f/${token}`, { skipAuth: true });
  },

  async verifyPassword(token: string, password: string): Promise<{ valid: boolean }> {
    return apiClient.post(`/f/${token}/verify`, { password }, { skipAuth: true });
  },

  async getForm(token: string, password?: string): Promise<PublicFormStructureResponse> {
    const headers: Record<string, string> = {};
    if (password) headers['X-Form-Password'] = password;
    return apiClient.get(`/f/${token}/form`, { skipAuth: true, headers });
  },

  async startResponse(
    token: string,
    data: StartResponseRequest,
    password?: string
  ): Promise<{ responseId: string }> {
    const headers: Record<string, string> = {};
    if (password) headers['X-Form-Password'] = password;
    return apiClient.post(`/f/${token}/start`, data, { skipAuth: true, headers });
  },

  async updateResponse(
    token: string,
    responseId: string,
    data: UpdateResponseRequest,
    password?: string
  ): Promise<{ success: boolean }> {
    const headers: Record<string, string> = {};
    if (password) headers['X-Form-Password'] = password;
    return apiClient.put(`/f/${token}/respond/${responseId}`, data, { skipAuth: true, headers });
  },

  async submitResponse(
    token: string,
    responseId: string,
    password?: string
  ): Promise<{ success: boolean }> {
    const headers: Record<string, string> = {};
    if (password) headers['X-Form-Password'] = password;
    return apiClient.post(`/f/${token}/submit/${responseId}`, {}, { skipAuth: true, headers });
  },

  async getDiagnostic(
    token: string,
    password?: string
  ): Promise<{ diagnostic: FormDiagnostic | null }> {
    const headers: Record<string, string> = {};
    if (password) headers['X-Form-Password'] = password;
    return apiClient.get(`/f/${token}/diagnostic`, { skipAuth: true, headers });
  },
};
